/** @module store/boards */

import { getEnergyManagementSystem } from '@/api/energy_management_systems'
import { apiCall } from '@/api/index'
import { apiV1UiBoardConfigPath, apiV1UiBoardConfigsPath } from '@/../lib/routes'
import boardStore from '@/store/modules/board'
import { translateAutoConfig } from '@/store/modules/board'
import { subscribeToCustomerEmsSensorMmChannels } from '@/api/action_cable'
import { Ems } from '@/store/modules/energy_management_systems'

const state = () => ({
  currentId: null,
  boards: {}
})

const FLAG_VALUES = {
  null: 0,
  default: 1,
  communityIuCustom: 2,
  communityIuAuto: 4,
  emsIuCustom: 8,
  emsIuAuto: 16
}

/**
 * A Board data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {integer} params.id is the BoardId
 * @param {integer} params.groupId is the Id of the Group
 * @param {integer} params.userId is the Id of the User
 * @param {integer} params.flag is the Board description. see FLAG_VALUES
 * @param {String} params.name is the Board name
 * @param {Integer}Arrday params.emsIds are the emsIds in the Board
 *
 */
export function Board({ id, groupId, userId, flag, name, emsIds }) {
  this.id = id
  this.groupId = groupId
  this.userId = userId
  this.flag = flag
  this.name = name
  this.emsIds = emsIds
}

const mutations = {
  /**
   * Mutation to reset the Boards store
   *
   * empties the boards state, sets the currentID to null and unregisters all boards
   *
   * @function
   *
   * @param {Store}
   *
   */
  RESET_BOARDS: (state, { store }) => {
    state.currentId = null
    for (const bId in state.boards) {
      store.unregisterModule(`board_${bId}`)
    }
    state.boards = {}
  },
  /**
   * Mutation to Set a new board
   *
   * adds the board to the state, sets the currentId on default and registers the new board with its config
   *
   * @function
   *
   * @param {Board}
   * @param {Group[]} data, a board config,
   * @param {Store}
   *
   */
  SET_BOARD: (state, { board, data, store, withMeasurements = true }) => {
    state.boards[board.id] = board
    if (board.flag & FLAG_VALUES.default) {
      state.currentId = board.id
    }
    if (data && store) {
      //registers a new board module for the created board
      store.registerModule(`board_${board.id}`, boardStore)
      if (withMeasurements) {
        store.commit(`board_${board.id}/SET_CONFIG_WITH_MEASUREMENTS`, data)
      }
    }
  },
  /**
   * Mutation to Set the current board ID
   *
   * @function
   *
   * @param {integer} id
   *
   */
  SET_CURRENT_BOARD_ID: (state, id) => {
    state.currentId = id
  },
  REMOVE_BOARD: (state, { id, store }) => {
    delete state.boards[id]
    if (Object.keys(state.boards).length) {
      state.currentId = null
      for (const bId in state.boards) {
        if (state.boards[bId].flag & FLAG_VALUES.emsIuAuto || state.boards[bId].flag & FLAG_VALUES.communityIuAuto) {
          continue
        } else {
          state.currentId = bId
          break
        }
      }
    } else {
      state.currentId = null
    }
    store.unregisterModule(`board_${id}`)
  },
  SET_BOARD_NAME: (state, { boardId, name }) => {
    if (state.boards[boardId]) {
      state.boards[boardId].name = name
    }
  },
  SET_BOARD_TO_DEFAULT: (state, id) => {
    if (state.boards[id]) {
      state.boards[id].flag = state.boards[id].flag | FLAG_VALUES.default
    }
  },
  SET_BOARD_TO_NOT_DEFAULT: (state, id) => {
    if (state.boards[id] && state.boards[id].flag & FLAG_VALUES.default) {
      state.boards[id].flag = state.boards[id].flag ^ FLAG_VALUES.default
    }
  }
}

const getters = {
  /**
   * Getter to get the Board information as select options
   *
   * Returns the boards for a specific group. All boards if groupId === null
   *
   * @function
   *
   * @params {integer} groupId
   *
   * @return {Object[]} Select Options
   */
  getBoardSelectOptions: (state) => {
    return (groupId = null, withoutAuto = false) => {
      const options = []
      for (const id in state.boards) {
        if (!groupId || (groupId && state.boards[id].groupId === groupId)) {
          if (
            withoutAuto &&
            (state.boards[id].flag & FLAG_VALUES.emsIuAuto || state.boards[id].flag & FLAG_VALUES.communityIuAuto)
          )
            continue

          options.push({
            label: state.boards[id].name,
            value: id
          })
        }
      }
      return options
    }
  },
  /**
   * Getter to get the current board store name
   *
   * if a currentId is set, return the name of the corresponding BoardStore
   *
   * @function
   *
   * @return {String} boardName
   */
  getCurrentBoardStoreName: (state) => () =>
    state.currentId && state.boards[state.currentId] ? `board_${state.currentId}` : null,
  /**
   * Getter to get the current board store name
   *
   * if a currentId is set, return the name of the corresponding BoardStore
   *
   * @function
   *
   * @return {String} boardName
   */
  getBoardStoreName: (state) => (boardId) => (state.boards[boardId] ? `board_${boardId}` : null),

  /**
   * Getter to get the currentBoardId
   *
   *
   * @function
   *
   * @return {integer} currentId
   */
  getCurrentBoardId: (state) => () => state.currentId,
  /**
   * Getter to get the EmsIds needed for a Board
   *
   * @function
   *
   * @params {integer} boardId
   *
   * @return {integer[]} emsIds
   */
  getEmsIds: (state) => {
    return (boardId) => {
      if (state.boards[boardId]) {
        return state.boards[boardId].emsIds
      }
      return null
    }
  },
  /**
   * Getter to see if the current Board is default
   *
   */
  currentBoardIsDefault: (state) => () => {
    if (!state.currentId) return false
    if (!state.boards[state.currentId]) return false
    if (state.boards[state.currentId].flag & FLAG_VALUES.default) return true

    // Auto is default if there is no default
    if (
      state.boards[state.currentId].flag & FLAG_VALUES.emsIuAuto ||
      state.boards[state.currentId].flag & FLAG_VALUES.communityIuAuto
    ) {
      for (const bId in state.boards) {
        if (state.boards[bId].flag & FLAG_VALUES.default) {
          return false
        }
      }
      return true
    }
    return false
  }
}

const actions = {
  /**
   * Action to toggle showChildren of a group in the current Board
   *
   * @function
   *
   * @params {Integer} gId, the GroupId
   */
  toggleCurrentShowChildren({ commit, getters }, gId) {
    const currentBoard = getters.getCurrentBoardStoreName()
    if (currentBoard) {
      commit(`${currentBoard}/TOGGLE_SHOW_CHILDREN`, gId, { root: true })
    }
  },
  /**
   * Action to get the Configs of an ems
   *
   * Gets the EmsAccessGroup of an EMS, and gets the corresponding Configs
   * Saving them afterwards in the BoardsStore, creating a BoardStore for each config
   *
   * @function
   *
   * @params {String} ctxt
   * @params {Store} store
   * @params {Integer} emsId
   *
   */
  getConfigsForEms({ commit }, { ctxt = 'customer', store, emsId, withAuto = true, withSubscription = false }) {
    let flag = FLAG_VALUES.default | FLAG_VALUES.emsIuCustom
    if (withAuto) {
      flag = flag | FLAG_VALUES.emsIuAuto
    }
    if (withSubscription) {
      subscribeToCustomerEmsSensorMmChannels({ emsIds: [emsId], store })
    }
    // 1. GET EMS
    // 2. extract EMS's EmsAccessGroup
    // 3. GET UI-board config for the EmsAccessGroup
    // 4. Update store
    //
    return getEnergyManagementSystem({
      id: emsId,
      ctxt,
      fields: ['name', 'ems_access_group']
    }).then((rsp) => {
      const emsGroupId = rsp.payload.data.relationships.emsAccessGroup.data.id
      commit('energyManagementSystemGroups/ADD_GROUPS_FOR_EMS', { emsId, emsGroupIds: [emsGroupId] }, { root: true })
      commit('energyManagementSystems/SET_EMS', new Ems({ id: emsId, name: rsp.payload.data.attributes.name }), {
        root: true
      })
      // Get Configs for the EmsAccessGroup
      const payload = {
        filter: {
          ui_board_configs: {
            flag,
            groupId: emsGroupId
          }
        }
      }

      apiCall({
        method: 'GET',
        path: apiV1UiBoardConfigsPath(ctxt),
        payload
      }).then((rsp) => {
        parseBoardData({ commit, data: rsp.payload.data, store })
      })
    })
  },
  /**
   * Action to get the Configs of a community
   *
   * Gets the rootGroupIds of all Communities and the corresponding Configs
   * Saving them afterwards in the BoardsStore, creating a BoardStore for each config
   *
   * @function
   *
   * @params {String} ctxt
   * @params {Store} store
   *
   */
  getConfigsForCommunity(
    { commit, dispatch },
    { ctxt = 'customer', communityId, store, withAuto = true, withSubscription = false }
  ) {
    let flag = FLAG_VALUES.default | FLAG_VALUES.communityIuCustom
    if (withAuto) {
      flag = flag | FLAG_VALUES.communityIuAuto
    }
    //get Community
    return dispatch('communities/getCommunity', { ctxt, communityId }, { root: true }).then((community) => {
      const groupId = community.rootGroupId

      const payload = {
        filter: {
          ui_board_configs: {
            flag,
            groupId
          }
        }
      }
      apiCall({
        method: 'GET',
        path: apiV1UiBoardConfigsPath(ctxt),
        payload
      }).then((rsp) => {
        parseBoardData({ commit, data: rsp.payload.data, store })

        if (withSubscription) {
          dispatch('subscribeToEmss', { store })
          dispatch('measurements/startInstantUpdateValuesRecencyTracker', undefined, { root: true })
        }
      })
    })
  },
  /**
   * Action to create a default Community Board
   *
   * Creates a Board, and adds it to Boards and sets it as the new currentBoard
   *
   * @function
   *
   * @params {Integer[]} userIds
   * @params {Integer[]} groupIds
   * @params {Integer} referenceUserId
   *
   * @return {Promise}
   */
  createCommunityBoard({ commit }, { store }) {
    //registers a new board module for the created board
    store.registerModule('board_1', boardStore)

    const board = new Board({
      id: 1,
      groupId: null,
      userId: null,
      flag: 0,
      name: 'Config',
      emsIds: []
    })
    commit('SET_BOARD', { board })
    commit('SET_CURRENT_BOARD_ID', 1)
  },
  /**
   * Action to trigger a store reset
   *
   * @function
   *
   * @params {Store} store
   */
  resetStore: ({ commit }, { store }) => {
    commit('RESET_BOARDS', { store })
  },
  createNewBoardConfig({ commit }, { name, emsIds, groupId, userId, mode, store }) {
    let flag = 0
    if (mode === 'community') {
      flag = flag | FLAG_VALUES.communityIuCustom
    }
    if (mode === 'ems') {
      flag = flag | FLAG_VALUES.emsIuCustom
    }

    const payload = {
      data: {
        type: 'ui_board_configs',
        attributes: {
          name,
          flag,
          emsIds,
          data: []
        },
        relationships: {
          user: {
            data: { type: 'users', id: userId.toString() }
          },
          group: {
            data: { type: 'groups', id: groupId.toString() }
          }
        }
      }
    }

    return apiCall({
      method: 'POST',
      path: apiV1UiBoardConfigsPath('customer'),
      payload
    }).then((rsp) => {
      const id = rsp.payload.data.id
      parseBoardData({ commit, data: rsp.payload.data, store, withMeasurements: false })
      commit('SET_CURRENT_BOARD_ID', id)
    })
  },
  updateCurrentBoardConfigName({ state, commit }, { name }) {
    if (!state.currentId) {
      return
    }
    return apiCall({
      method: 'PATCH',
      path: apiV1UiBoardConfigPath('customer', state.currentId),
      payload: {
        data: {
          type: 'ui_board_configs',
          id: state.currentId,
          attributes: {
            name
          }
        }
      }
    }).then(() => {
      commit('SET_BOARD_NAME', { boardId: state.currentId, name })
    })
  },
  updateCurrentBoardConfigData({ state, getters }, { store }) {
    const currentBoard = getters.getCurrentBoardStoreName()
    const data = store.getters[`${currentBoard}/config`]()

    if (!data) {
      return
    }

    return apiCall({
      method: 'PATCH',
      path: apiV1UiBoardConfigPath('customer', state.currentId),
      payload: {
        data: {
          type: 'ui_board_configs',
          id: state.currentId,
          attributes: {
            data
          }
        }
      }
    })
  },
  deleteCurrentBoardConfig({ state, commit }, { store }) {
    return apiCall({
      method: 'DELETE',
      path: apiV1UiBoardConfigPath('customer', state.currentId),
      payload: {
        data: {
          type: 'ui_board_configs',
          id: state.currentId
        }
      }
    }).then(() => {
      const deletedId = state.currentId
      commit('REMOVE_BOARD', { id: state.currentId, store })
      return deletedId
    })
  },
  setCurrentBoardConfigAsDefault({ state, commit }) {
    const promises = []
    if (state.currentId) {
      for (const id in state.boards) {
        if (state.boards[id].flag & FLAG_VALUES.emsIuAuto || state.boards[id].flag & FLAG_VALUES.communityIuAuto) {
          continue
        } else if (id === state.currentId) {
          promises.push(
            apiCall({
              method: 'PATCH',
              path: apiV1UiBoardConfigPath('customer', id),
              payload: {
                data: {
                  type: 'ui_board_configs',
                  id: state.currentId,
                  attributes: {
                    flag: state.boards[id].flag | FLAG_VALUES.default
                  }
                }
              }
            }).then(() => {
              commit('SET_BOARD_TO_DEFAULT', id)
            })
          )
        } else if (state.boards[id].flag & FLAG_VALUES.default) {
          promises.push(
            apiCall({
              method: 'PATCH',
              path: apiV1UiBoardConfigPath('customer', id),
              payload: {
                data: {
                  type: 'ui_board_configs',
                  id: state.currentId,
                  attributes: {
                    flag: state.boards[id].flag ^ FLAG_VALUES.default
                  }
                }
              }
            }).then(() => {
              commit('SET_BOARD_TO_NOT_DEFAULT', id)
            })
          )
        }
      }
    }
    return Promise.all(promises)
  },
  setCurrentBoardConfigToAuto({ state, commit, getters, rootGetters }) {
    const currentBoard = getters.getCurrentBoardStoreName()
    let autoBoard = null
    for (const bId in state.boards) {
      if (state.boards[bId].flag & FLAG_VALUES.emsIuAuto || state.boards[bId].flag & FLAG_VALUES.communityIuAuto) {
        autoBoard = getters.getBoardStoreName(bId)
      }
    }

    if (autoBoard) {
      const autoConfig = rootGetters[`${autoBoard}/config`]()

      const deepcopy = JSON.parse(JSON.stringify(autoConfig))

      commit(`${currentBoard}/SET_CONFIG`, translateAutoConfig(deepcopy), { root: true })
    }
  },
  subscribeToEmss({ state }, { store }) {
    for (const bId in state.boards) {
      if (state.boards[bId].flag & FLAG_VALUES.emsIuAuto || state.boards[bId].flag & FLAG_VALUES.communityIuAuto) {
        subscribeToCustomerEmsSensorMmChannels({ emsIds: state.boards[bId].emsIds, store })
      }
    }
  }
}

const parseBoardData = ({ commit, data, store, withMeasurements = true }) => {
  if (!Array.isArray(data)) {
    data = [data]
  }
  for (const config of data) {
    const board = new Board({
      id: config.id,
      groupId: config.relationships?.group.data.id,
      userId: config.relationships?.user.data.id,
      flag: config.attributes.flag,
      name: config.attributes.name,
      emsIds: config.attributes.emsIds
    })
    commit('SET_BOARD', { board, data: config.attributes.data, store, withMeasurements })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
